import { useEffect, useState } from "react";
import { User, Package, CreditCard, ShoppingBag, Star, Settings as SettingsIcon, LogOut } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Avatar, AvatarFallback } from "@baluu/ui/components/avatar";
import { Separator } from "@baluu/ui/components/separator";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@baluu/ui/components/tabs";
import { CustomerAuthDialog } from "../CustomerAuthDialog/CustomerAuthDialog";
import { useAuthSnapshot, hydrateSession, signOutCustomer } from "~/widgets/lib/auth-client";
import type { AuthUser } from "~/widgets/stores/auth-store";
import { useProfileData } from "./useProfileData";
import { formatMonthYear } from "./format";
import { initialTabOf } from "./initialTab";
import { OverviewTab } from "./tabs/OverviewTab";
import { BundlesTab } from "./tabs/BundlesTab";
import { MembershipsTab } from "./tabs/MembershipsTab";
import { PurchasesTab } from "./tabs/PurchasesTab";
import { ActivityTab } from "./tabs/ActivityTab";
import { SettingsTab } from "./tabs/SettingsTab";
import type { ProfileData } from "./types";

export type ProfileProps = {
  businessId?: string;
  businessSlug?: string;
  apiUrl?: string;
  widgetBaseUrl?: string;
  /** Accepted for parity with the other widgets' r2wc props map (see
   * index.ts) — not read here yet; theming is driven by `primaryColor` +
   * the school's theme overrides (lib/theme.ts), same as the rest of this
   * app's components that also accept but don't yet act on `theme`
   * (e.g. CheckoutOverlay.tsx). */
  theme?: string;
  primaryColor?: string;
  /** The tab it opens at (`initial-tab`): overview (the default), bundles,
   * memberships, purchases, activity or settings. The hosted site's
   * /purchases and /settings pages open the account here. */
  initialTab?: string;
};

/** Absolute link into the hosted widget for a purchase row. Every href in
 * this tree goes through this instead of a relative path — Profile.tsx is
 * embedded on the PARTNER's own site, so a relative `/bookings/manage/x`
 * would resolve against the partner's origin, not the hosted widget's.
 * Paths mirror ProfileView.tsx's `orderURL` (:775-787) / bundle "Manage"
 * link (:227). */
export type ManageUrl = (kind: "bookings" | "bundles" | "products" | "giftcards", id: string) => string;

function buildManageUrl(widgetBaseUrl?: string): ManageUrl {
  const base = (widgetBaseUrl || "").replace(/\/+$/, "");
  return (kind, id) => {
    switch (kind) {
      case "bookings":
        return `${base}/bookings/manage/${id}`;
      case "bundles":
        return `${base}/bundles/manage/${id}`;
      case "products":
        return `${base}/payment/product/success/${id}`;
      case "giftcards":
        return `${base}/giftcards/${id}`;
    }
  };
}

/** Ported from ProfileView.tsx's `ProfileHeader` (:124-164). Falls back to
 * the already-known `authUser` (from the auth store, available the instant
 * sign-in completes) while `/widget/me/profile` is still loading, so the
 * header never has to show a bare "User" placeholder for a signed-in
 * customer. */
function ProfileHeader({
  profile,
  authUser,
  onSignOut,
}: {
  profile?: ProfileData;
  authUser: AuthUser | null;
  onSignOut: () => void;
}) {
  const name = profile?.name || authUser?.name;
  const email = profile?.email || authUser?.email;
  const displayName = name || email || "User";
  const initials = (name?.charAt(0) || email?.charAt(0) || "U").toUpperCase();

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarFallback className="bg-primary text-xl text-primary-foreground">{initials}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-xl font-bold">{displayName}</h1>
          {email && <p className="text-sm text-muted-foreground">{email}</p>}
          {profile?.createdAt && <p className="text-xs text-muted-foreground">Member since {formatMonthYear(profile.createdAt)}</p>}
        </div>
      </div>
      <Button variant="destructive" onClick={onSignOut} className="flex items-center gap-2">
        <LogOut className="h-4 w-4" />
        Sign out
      </Button>
    </div>
  );
}

/**
 * Signed-in profile shell: header + the 6 tabs. Split out from `Profile` so
 * `useProfileData` (which calls 5 `useQuery`s + 3 `useMutation`s) is only
 * ever mounted — and therefore only ever called — once the customer is
 * actually authenticated. Calling it unconditionally at the top of `Profile`
 * and gating on `auth.status` with an early return above it would violate
 * the Rules of Hooks: the signed-out render calls fewer hooks than the
 * signed-in render of the SAME component instance, which React does not
 * allow across re-renders of one component (e.g. the moment sign-in
 * completes and `auth.status` flips from "anonymous"/"unknown" to
 * "authenticated" without a remount).
 */
function SignedInProfile(p: ProfileProps & { token: string; authUser: AuthUser | null }) {
  const data = useProfileData({
    apiUrl: p.apiUrl,
    businessId: p.businessId ?? "",
    businessSlug: p.businessSlug ?? "",
    token: p.token,
  });
  const manageUrl = buildManageUrl(p.widgetBaseUrl);

  const allOrders = data.purchases.data?.data.allOrders ?? [];
  const memberships = data.memberships.data?.data ?? [];
  const hasMemberships = memberships.some((m) => m.status !== "cancelled");

  // The tab it opens at (initial-tab) follows the memberships as they load
  // (the Memberships tab shows only with one) until the visitor picks a
  // tab; then theirs stays. A tab that goes away (the last membership
  // cancelled) falls back to Overview.
  const opening = initialTabOf(p.initialTab, hasMemberships);
  const [picked, setPicked] = useState<string | null>(null);
  const tab = picked === "memberships" && !hasMemberships ? "overview" : (picked ?? opening);
  const chooseTab = (value: string) => setPicked(value);

  return (
    <div className="space-y-4">
      <ProfileHeader
        profile={data.profile.data?.data}
        authUser={p.authUser}
        onSignOut={() => void signOutCustomer(p.apiUrl, p.businessSlug)}
      />
      <Separator />
      <Tabs value={tab} onValueChange={chooseTab}>
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" className="gap-1.5">
            <User className="h-4 w-4" />
            <span>Overview</span>
          </TabsTrigger>
          <TabsTrigger value="bundles" className="gap-1.5">
            <Package className="h-4 w-4" />
            <span>Bundles</span>
          </TabsTrigger>
          {hasMemberships && (
            <TabsTrigger value="memberships" className="gap-1.5">
              <CreditCard className="h-4 w-4" />
              <span>Memberships</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="purchases" className="gap-1.5">
            <ShoppingBag className="h-4 w-4" />
            <span>Purchases</span>
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-1.5">
            <Star className="h-4 w-4" />
            <span>Activity</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-1.5">
            <SettingsIcon className="h-4 w-4" />
            <span>Settings</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            allOrders={allOrders}
            isLoading={data.purchases.isPending}
            isError={!!data.purchases.error}
            onRetry={() => void data.purchases.refetch()}
            manageUrl={manageUrl}
          />
        </TabsContent>

        <TabsContent value="bundles">
          <BundlesTab
            allOrders={allOrders}
            isLoading={data.purchases.isPending}
            isError={!!data.purchases.error}
            onRetry={() => void data.purchases.refetch()}
            manageUrl={manageUrl}
          />
        </TabsContent>

        {hasMemberships && (
          <TabsContent value="memberships">
            <MembershipsTab
              memberships={memberships}
              isLoading={data.memberships.isPending}
              isError={!!data.memberships.error}
              onRetry={() => void data.memberships.refetch()}
              onManage={(id) => data.openBillingPortal.mutate(id)}
              isManaging={data.openBillingPortal.isPending}
              managingId={data.openBillingPortal.variables}
              manageError={data.openBillingPortal.isError}
            />
          </TabsContent>
        )}

        <TabsContent value="purchases">
          <PurchasesTab
            allOrders={allOrders}
            isLoading={data.purchases.isPending}
            isError={!!data.purchases.error}
            onRetry={() => void data.purchases.refetch()}
            manageUrl={manageUrl}
          />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab
            waitlist={data.waitlist.data?.data.waitlist ?? []}
            waitlistLoading={data.waitlist.isPending}
            waitlistError={!!data.waitlist.error}
            onRetryWaitlist={() => void data.waitlist.refetch()}
            onLeaveWaitlist={(id) => data.leaveWaitlist.mutate(id)}
            leavingId={data.leaveWaitlist.variables}
            leaveError={data.leaveWaitlist.isError}
            reviews={data.reviews.data?.data.reviews ?? []}
            reviewsLoading={data.reviews.isPending}
            reviewsError={!!data.reviews.error}
            onRetryReviews={() => void data.reviews.refetch()}
          />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab
            profile={data.profile.data?.data}
            isLoading={data.profile.isPending}
            isError={!!data.profile.error}
            onRetry={() => void data.profile.refetch()}
            onSave={(patch) => data.updateProfile.mutate(patch)}
            isSaving={data.updateProfile.isPending}
            saveError={data.updateProfile.isError}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function Profile(p: ProfileProps) {
  const auth = useAuthSnapshot();
  useEffect(() => {
    void hydrateSession(p.apiUrl, p.businessSlug);
  }, [p.apiUrl, p.businessSlug]);
  const [authOpen, setAuthOpen] = useState(false);

  if (auth.status !== "authenticated" || !auth.token) {
    return (
      <div className="space-y-3 rounded-lg border p-6 text-center">
        <p className="text-sm text-muted-foreground">Sign in to see your bookings, memberships and details.</p>
        <Button onClick={() => setAuthOpen(true)} disabled={auth.status === "unknown"}>
          Sign in
        </Button>
        <CustomerAuthDialog
          open={authOpen}
          onOpenChange={setAuthOpen}
          apiUrl={p.apiUrl}
          businessSlug={p.businessSlug}
          primaryColor={p.primaryColor}
        />
      </div>
    );
  }

  return <SignedInProfile {...p} token={auth.token} authUser={auth.user} />;
}

export default Profile;
